import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { toast } from 'react-toastify';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { WizardStepBar } from '@/components/ui/wizard-step-bar';
import { useChatSidebar } from 'components/GlobalChatSidebar/ChatSidebarProvider';
import useIsOrgAdmin from 'hooks/useIsOrgAdmin';
import { useAorText } from 'utils/aorTerminology';
import {
  ONBOARDING_PARAM,
  askAiPrompt,
  isOnboardingStepId,
  stepStatus,
  type OnboardingAction,
  type OnboardingDataStep,
  type OnboardingStepId,
} from './onboardingModel';
import {
  useOrgOnboardingAction,
  useOrgOnboardingProgress,
} from './useOrgOnboarding';
import { AorStep } from './steps/AorStep';
import { AssetStep } from './steps/AssetStep';
import { FinishStep } from './steps/FinishStep';
import { RuleStep } from './steps/RuleStep';
import { StepFooter } from './steps/StepFooter';

const NEXT: Record<OnboardingDataStep, OnboardingStepId> = {
  aors: 'assets',
  assets: 'rules',
  rules: 'finish',
};

/**
 * Organization setup: define AORs, import assets, create automation rules.
 * Opened from the hub menu with `?onboarding=1` (or a step id).
 */
export function OnboardingSheet() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get(ONBOARDING_PARAM);
  const open = requested !== null;
  const aorText = useAorText();
  const isAdmin = useIsOrgAdmin();
  const { queueComposerPrompt } = useChatSidebar();
  const { state, counts, skipped, progress, isLoading } =
    useOrgOnboardingProgress({ enabled: open });
  const action = useOrgOnboardingAction();
  const [step, setStep] = useState<OnboardingStepId>('aors');
  const placedRef = useRef(false);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!open) {
      placedRef.current = false;
      return;
    }
    if (isOnboardingStepId(requested)) {
      setStep(requested);
      placedRef.current = true;
      return;
    }
    if (placedRef.current || isLoading) return;
    placedRef.current = true;
    setStep(progress.nextStep);
  }, [open, requested, isLoading, progress.nextStep]);

  useEffect(() => {
    if (!open || !isAdmin || !state || startedRef.current) return;
    startedRef.current = true;
    if (!state.started_at) action.mutate({ action: 'start' });
  }, [open, isAdmin, state, action]);

  const close = () => {
    setSearchParams(
      (params) => {
        params.delete(ONBOARDING_PARAM);
        return params;
      },
      { replace: true }
    );
  };

  const run = (next: OnboardingAction, onDone?: () => void) => {
    action.mutate(next, {
      onSuccess: onDone,
      onError: () => toast.error('Could not save organization setup.'),
    });
  };

  const askAi = (dataStep: OnboardingDataStep) => {
    queueComposerPrompt(askAiPrompt(dataStep, aorText('AORs')));
    close();
  };

  const steps = [
    {
      id: 'aors' as const,
      label: aorText('AORs'),
      subtitle: subtitle('aors'),
      complete: counts.aors > 0,
    },
    {
      id: 'assets' as const,
      label: 'Assets',
      subtitle: subtitle('assets'),
      complete: counts.assets > 0,
    },
    {
      id: 'rules' as const,
      label: 'Rules',
      subtitle: subtitle('rules'),
      complete: counts.rules > 0,
    },
    {
      id: 'finish' as const,
      label: 'Finish',
      subtitle: state?.completed_at ? 'Complete' : progress.label,
      complete: Boolean(state?.completed_at),
    },
  ];

  function subtitle(dataStep: OnboardingDataStep): string {
    const status = stepStatus(dataStep, counts, skipped);
    if (status === 'skipped') return 'Skipped';
    return `${counts[dataStep]} added`;
  }

  return (
    <Sheet open={open} onOpenChange={(next) => (next ? undefined : close())}>
      <SheetContent
        side="left"
        className="dark tw:w-full tw:gap-0 tw:p-0 tw:text-foreground tw:sm:!max-w-xl"
      >
        <SheetHeader className="tw:border-b tw:border-border tw:px-5 tw:py-4">
          <SheetTitle>Organization setup</SheetTitle>
          <SheetDescription>
            {aorText(
              'Set up your AORs, assets, and automation rules so Pratus can watch for hazards that affect you.'
            )}
          </SheetDescription>
          <WizardStepBar
            className="tw:pt-2"
            label="Organization setup steps"
            steps={steps}
            activeId={step}
            onStepChange={setStep}
          />
        </SheetHeader>
        <div className="tw:flex tw:min-h-0 tw:flex-1 tw:flex-col tw:gap-4 tw:overflow-y-auto tw:px-5 tw:py-4">
          {!isAdmin ? (
            <p className="tw:m-0 tw:rounded-md tw:border tw:border-border tw:px-3 tw:py-2 tw:text-xs tw:text-muted-foreground">
              Only org admins can change organization setup.
            </p>
          ) : null}
          {step === 'finish' ? (
            <FinishStep
              counts={counts}
              skipped={skipped}
              completedAt={state?.completed_at ?? null}
              canEdit={isAdmin}
              completing={action.isPending}
              onComplete={() =>
                state?.completed_at
                  ? close()
                  : run({ action: 'complete' }, () => {
                      toast.success('Organization setup complete.');
                      close();
                    })
              }
              onRestart={() =>
                run({ action: 'restart' }, () => setStep('aors'))
              }
              onNavigate={close}
            />
          ) : (
            <>
              <div className="tw:min-h-0 tw:flex-1">
                {step === 'aors' ? <AorStep /> : null}
                {step === 'assets' ? <AssetStep /> : null}
                {step === 'rules' ? <RuleStep /> : null}
              </div>
              <StepFooter
                status={stepStatus(step, counts, skipped)}
                canEdit={isAdmin}
                onSkip={() =>
                  run({ action: 'skip', step }, () => setStep(NEXT[step]))
                }
                onUnskip={() => run({ action: 'unskip', step })}
                onAskAi={() => askAi(step)}
                onNext={() => setStep(NEXT[step])}
                nextLabel={step === 'rules' ? 'Review' : 'Next'}
              />
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
